import { useState, useEffect } from "react";
import {
  getAllCountries,
  getRegionsByCountry,
  getDistrictsByRegion,
  getTehsilsByDistrict,
  createCity,
} from "../Api/CityMaster";
import {
  Accordion,
  AccordionItem,
  AccordionBody,
  Container,
  Form,
  Button,
  Row,
  Col,
} from "react-bootstrap";

import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import "../Styling/Title.css";
import "../Styling/Accordion.css";
import "../Styling/SaveButton.css";

const CityMaster = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);

  const [cityData, setCityData] = useState({
    code: "",
    description: "",
    country: "",
    region: "",
    district: "",
    tehsil: "",
  });

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await getAllCountries();
        setCountries(data);
      } catch (error) {
        toast.error("🦄 Failed to load countries!");
      }
    };
    fetchCountries();
  }, []);

  // Fetch regions when country changes
  useEffect(() => {
    const fetchRegions = async () => {
      if (cityData.country) {
        try {
          const data = await getRegionsByCountry(cityData.country);
          setRegions(data);
          // Reset dependent fields when country changes
          setCityData((prev) => ({
            ...prev,
            region: "",
            district: "",
            tehsil: "",
          }));
          setDistricts([]);
          setTehsils([]);
        } catch (error) {
          toast.error("🦄 Failed to load regions!");
        }
      } else {
        setRegions([]);
        setDistricts([]);
        setTehsils([]);
        setCityData((prev) => ({
          ...prev,
          region: "",
          district: "",
          tehsil: "",
        }));
      }
    };
    fetchRegions();
  }, [cityData.country]);

  // Fetch districts when region changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (cityData.region) {
        try {
          const data = await getDistrictsByRegion(cityData.region);
          setDistricts(data);
          // Reset dependent fields when region changes
          setCityData((prev) => ({
            ...prev,
            district: "",
            tehsil: "",
          }));
          setTehsils([]);
        } catch (error) {
          toast.error("🦄 Failed to load districts!");
        }
      } else {
        setDistricts([]);
        setTehsils([]);
        setCityData((prev) => ({
          ...prev,
          district: "",
          tehsil: "",
        }));
      }
    };
    fetchDistricts();
  }, [cityData.region]);

  // Fetch tehsils when district changes
  useEffect(() => {
    const fetchTehsils = async () => {
      if (cityData.district) {
        try {
          const data = await getTehsilsByDistrict(cityData.district);
          setTehsils(data);
          // Reset tehsil when district changes
          setCityData((prev) => ({
            ...prev,
            tehsil: "",
          }));
        } catch (error) {
          toast.error("🦄 Failed to load tehsils!");
        }
      } else {
        setTehsils([]);
        setCityData((prev) => ({
          ...prev,
          tehsil: "",
        }));
      }
    };
    fetchTehsils();
  }, [cityData.district]);

  const handleChange = (e) => {
    setCityData({ ...cityData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createCity(cityData);
      toast.success("🦄 City Added Successfully");
      setCityData({
        code: "",
        description: "",
        country: "",
        region: "",
        district: "",
        tehsil: "",
      });
    } catch (error) {
      toast.error("🦄 Some Error Happened");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="container city-master-fields">
        <h2 className="fields-title">City Master</h2>

        <Form onSubmit={handleSubmit} className="fields-form">
          <Accordion defaultActiveKey="0" className="city-master-accordion">
            <AccordionItem eventKey="0">
              <AccordionBody>
                <Row className="">
                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        City Code <span className="text-red">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="code"
                        value={cityData.code}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Description <span className="text-red">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="description"
                        value={cityData.description}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Country Code <span className="text-red">*</span>
                      </Form.Label>
                      <Form.Select
                        name="country"
                        value={cityData.country}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option
                            key={country.countryCode}
                            value={country.countryCode}
                          >
                            {country.countryCode} - {country.countryName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Region Code <span className="text-red">*</span>
                      </Form.Label>
                      <Form.Select
                        name="region"
                        value={cityData.region}
                        onChange={handleChange}
                        required
                        disabled={!cityData.country}
                      >
                        <option value="">Select Region</option>
                        {regions.map((region) => (
                          <option
                            key={region.regionCode}
                            value={region.regionCode}
                          >
                            {region.regionCode} - {region.regionName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        District Code <span className="text-red">*</span>
                      </Form.Label>
                      <Form.Select
                        name="district"
                        value={cityData.district}
                        onChange={handleChange}
                        required
                        disabled={!cityData.region}
                      >
                        <option value="">Select District</option>
                        {districts.map((district) => (
                          <option key={district.code} value={district.code}>
                            {district.code} - {district.description}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Tehsil Code <span className="text-red">*</span>
                      </Form.Label>
                      <Form.Select
                        name="tehsil"
                        value={cityData.tehsil}
                        onChange={handleChange}
                        required
                        disabled={!cityData.district}
                      >
                        <option value="">Select Tehsil</option>
                        {tehsils.map((tehsil) => (
                          <option key={tehsil.code} value={tehsil.code}>
                            {tehsil.code} - {tehsil.description}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <div className="fields-btn-container" style={{ marginTop: 16 }}>
            <Button variant="primary" type="submit" className="fields-submit">
              Save City
            </Button>
          </div>
        </Form>
      </div>

      {/* Loader Overlay with Sample 14 */}
      {loading && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default CityMaster;
